import { CheckExpandable } from '@/components/CheckExpandable';
import { SectionHeader } from '@/components/SectionHeader';
import { useFormSyncEditing } from '@/hooks/useFormSyncEditing';
import { Button, Card, Form, Popover } from '@arco-design/web-react';
import { IconFile, IconPlus } from '@arco-design/web-react/icon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { GrayBox } from '../tools';
import { EnvItem, useEnvironmentVarStore } from './_hooks/useEnvironmentVarStore';
import { EnvironmentVariableItem } from './environment-variable-item';
import { ReferenceData, ReferenceSelect } from './reference-select';

const FormList = Form.List;

export interface EnvFormData {
  env: EnvItem[];
}

const initialValues: EnvFormData = {
  env: undefined,
};

export function EnvironmentVariable({ visible }: { visible?: boolean }) {
  const { t } = useTranslation();
  const msg = {
    environmentVariable: t('common:environment'),
    environmentVariableDesc: t('components:add.environment.variables.for.containers'),
    key: t('common:key'),
    value: t('common:value'),
    add: t('common:add'),
    batchReference: t('components:batch.reference'),
  };

  const [form] = Form.useForm<EnvFormData>();
  const {
    isEnvironmentVarSelected,
    toggleEnvironmentVarSelected,
    env,
    updateEnv,
    bulkUpdateEnv,
    setValidateEnv,
  } = useEnvironmentVarStore();
  useFormSyncEditing({ data: { env }, form, trigger: !!env?.length });
  // 添加一个状态用于检查是否有空项
  const [hasEmptyItem, setHasEmptyItem] = useState(false);
  const [referenceVisible, setReferenceVisible] = useState(false);

  // 关闭时重置表单
  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible, form]);

  // arco 的这个 popover 竟然受控关闭会自动滚动到顶部, 所以这里强制转移焦点来解决这个问题
  const batchReferenceButtonRef = useRef<HTMLButtonElement>(null);

  const validateEnv = useCallback(async () => {
    try {
      await form.validate();
      return true;
    } catch (error) {
      return false;
    }
  }, [form]);

  useEffect(() => {
    setValidateEnv(validateEnv);
  }, [validateEnv, setValidateEnv]);

  const handleReferenceCancel = () => {
    setReferenceVisible(false);
    batchReferenceButtonRef.current?.focus();
  };

  const handleReferenceOk = (data: ReferenceData) => {
    const { type, selectedKeys, selectedMap } = data;
    if (!selectedKeys.length) {
      return;
    }
    const envs = selectedKeys.map((key) => {
      return {
        type,
        key,
        resource: selectedMap,
        resourceKey: key,
      };
    });
    bulkUpdateEnv(envs);
    setReferenceVisible(false);
    batchReferenceButtonRef.current?.focus();
  };

  const Header = (
    <SectionHeader
      inline
      title={msg.environmentVariable}
      description={msg.environmentVariableDesc}
    />
  );
  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={(_, values) => {
        updateEnv(values.env);
        // 检查是否有任何一项的 key 或 value 为空
        const isEmpty = values.env?.some(
          (item) => !item?.key || (!item?.value && (!item?.resource || !item?.resourceKey)),
        );
        setHasEmptyItem(isEmpty);
      }}
      initialValues={initialValues}
    >
      <Card>
        <CheckExpandable
          label={Header}
          checked={isEnvironmentVarSelected}
          onChange={toggleEnvironmentVarSelected}
        >
          <Content>
            <GrayBox>
              <FormList field="env">
                {(fields, { add, remove }) => {
                  return (
                    <div>
                      {fields.map((item, index) => {
                        return (
                          <EnvironmentVariableItem
                            key={index}
                            field={item.field}
                            index={index}
                            remove={remove}
                            form={form}
                            data={env}
                          />
                        );
                      })}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          icon={<IconPlus />}
                          type="primary"
                          size="mini"
                          disabled={hasEmptyItem}
                          onClick={() => add({ type: 'custom' })}
                        >
                          {msg.add}
                        </Button>
                        <StyledPopover
                          position="right"
                          trigger="click"
                          popupVisible={referenceVisible}
                          triggerProps={{ autoFixPosition: false }}
                          onVisibleChange={(b) => {
                            if (b) {
                              setReferenceVisible(true);
                            } else {
                              setReferenceVisible(false);
                            }
                          }}
                          content={
                            <ReferenceSelect
                              onCancel={handleReferenceCancel}
                              onOk={handleReferenceOk}
                            />
                          }
                        >
                          <Button
                            icon={<IconFile />}
                            type="primary"
                            size="mini"
                            ref={batchReferenceButtonRef}
                          >
                            {msg.batchReference}
                          </Button>
                        </StyledPopover>
                      </div>
                    </div>
                  );
                }}
              </FormList>
            </GrayBox>
          </Content>
        </CheckExpandable>
      </Card>
    </Form>
  );
}

/** ============================ 样式 ==================================== */
const Content = styled.div`
  margin-top: 1rem;
`;

const StyledPopover = styled(Popover)`
  .arco-popover-content {
    width: 520px;
  }
`;
